import React, { useEffect, useMemo, useState } from "react";
import { useMsal } from "@azure/msal-react";
import DataTable, { TableColumn } from "react-data-table-component";
import { Calculator, Download, Info, TrendingUp, BarChart3 } from "lucide-react";
import * as XLSX from "xlsx";
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    LineElement,
    PointElement,
    Title,
    Tooltip,
    Legend,
    Filler,
} from "chart.js";
import { Line, Bar } from "react-chartjs-2";
import { getApiBaseUrl } from "../utils/baseUrl";
import { useLoading, acquireTokenSafe } from "../App";

ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    LineElement,
    PointElement,
    Title,
    Tooltip,
    Legend,
    Filler
);

// ── Stage configuration ─────────────────────────────────────
// Order matters: drives KPI strip, chart legend, table columns.
type StageKey =
    | "prepared"
    | "initialReview"
    | "managerReview"
    | "sentForApproval"
    | "approved"
    | "filed";

const STAGES: Array<{ key: StageKey; label: string; short: string; color: string }> = [
    { key: "prepared",        label: "Accounts Prepared",            short: "Prepared",         color: "#3b82f6" },
    { key: "initialReview",   label: "Initial Review Completed",     short: "Initial Review",   color: "#8b5cf6" },
    { key: "managerReview",   label: "Managers Review Completed",    short: "Manager Review",   color: "#ec4899" },
    { key: "sentForApproval", label: "Accounts Sent for Approval",   short: "Sent for Approval", color: "#f59e0b" },
    { key: "approved",        label: "Accounts Approved",            short: "Approved",         color: "#10b981" },
    { key: "filed",           label: "Tax Return Filed",             short: "Filed",            color: "#14b8a6" },
];

// ── Row type + API response mapping ─────────────────────────

type Row = {
    month: string;            // e.g. 'Jan-23'
    monthDate: Date;          // first of month, used for sorting / filtering
    year: number;
    prepared: number;
    initialReview: number;
    managerReview: number;
    sentForApproval: number;
    approved: number;
    filed: number;
    total: number;
};

const MONTH_NAMES = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

type ApiRow = {
    month: string;             // 'YYYY-MM'
    accounts_prepared: string | number;
    initial_review: string | number;
    managers_review: string | number;
    sent_for_approval: string | number;
    accounts_approved: string | number;
    tax_return_filed: string | number;
};

/** Map the API response (YYYY-MM strings + per-stage counts) to UI Row shape. */
const mapApiRows = (apiRows: ApiRow[]): Row[] =>
    apiRows
        .filter((r) => r && r.month && /^\d{4}-\d{2}$/.test(r.month))
        .map((r) => {
            const [yearStr, monthStr] = r.month.split("-");
            const year = parseInt(yearStr, 10);
            const monthIdx0 = parseInt(monthStr, 10) - 1;
            const num = (v: string | number) => (v === null || v === undefined ? 0 : Number(v) || 0);
            const prepared = num(r.accounts_prepared);
            const initialReview = num(r.initial_review);
            const managerReview = num(r.managers_review);
            const sentForApproval = num(r.sent_for_approval);
            const approved = num(r.accounts_approved);
            const filed = num(r.tax_return_filed);
            return {
                month: `${MONTH_NAMES[monthIdx0]}-${String(year).slice(-2)}`,
                monthDate: new Date(year, monthIdx0, 1),
                year,
                prepared,
                initialReview,
                managerReview,
                sentForApproval,
                approved,
                filed,
                total: prepared + initialReview + managerReview + sentForApproval + approved + filed,
            };
        })
        .sort((a, b) => a.monthDate.getTime() - b.monthDate.getTime());

// ── Custom DataTable styles (matches /enhanced-timekeeping, /acquisition-chargebacks) ──

const customStyles = {
    table: { style: { backgroundColor: "var(--bg-secondary)", borderRadius: "8px" } },
    headRow: {
        style: {
            backgroundColor: "var(--bg-tertiary)",
            borderBottomColor: "var(--border-color)",
            color: "var(--text-primary)",
            fontWeight: 600,
            fontSize: "0.85rem",
        },
    },
    headCells: { style: { color: "var(--text-secondary)", paddingLeft: "16px", paddingRight: "16px" } },
    rows: {
        style: {
            backgroundColor: "var(--bg-secondary)",
            color: "var(--text-primary)",
            borderBottomColor: "var(--border-color)",
        },
        highlightOnHoverStyle: {
            backgroundColor: "var(--bg-hover)",
            color: "var(--text-primary)",
            borderBottomColor: "var(--border-color)",
        },
    },
    cells: { style: { color: "var(--text-primary)", paddingLeft: "16px", paddingRight: "16px" } },
    pagination: {
        style: {
            backgroundColor: "var(--bg-secondary)",
            color: "var(--text-secondary)",
            borderTopColor: "var(--border-color)",
        },
        pageButtonsStyle: {
            color: "var(--text-secondary)",
            fill: "var(--text-secondary)",
            "&:hover:not(:disabled)": { backgroundColor: "var(--bg-hover)" },
            "&:disabled": { opacity: 0.4 },
        },
    },
    noData: { style: { backgroundColor: "var(--bg-secondary)", color: "var(--text-secondary)" } },
};

// ── Main component ──────────────────────────────────────────

const CorporationTaxTracker: React.FC = () => {
    const { instance, accounts } = useMsal();
    const { setLoading } = useLoading();
    const [allRows, setAllRows] = useState<Row[]>([]);
    const [error, setError] = useState<string | null>(null);
    const [showHelp, setShowHelp] = useState(false);
    const [yearFilter, setYearFilter] = useState<number | "all">("all");

    useEffect(() => {
        const fetchData = async () => {
            const token = await acquireTokenSafe(instance, accounts);
            if (!token) return;
            setLoading(true);
            setError(null);
            try {
                const res = await fetch(`${getApiBaseUrl()}/api/corporation-tax-tracker/data`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                if (!res.ok) throw new Error(`Request failed (${res.status})`);
                const data: ApiRow[] = await res.json();
                setAllRows(mapApiRows(data));
            } catch (err: any) {
                setError(err?.message || "Failed to load data");
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [instance, accounts, setLoading]);

    const years = useMemo(() => {
        const ys = Array.from(new Set(allRows.map((r) => r.year))).sort();
        return ys;
    }, [allRows]);

    const filtered = useMemo(
        () => (yearFilter === "all" ? allRows : allRows.filter((r) => r.year === yearFilter)),
        [allRows, yearFilter]
    );

    // KPI totals for the selected filter window.
    const totals = useMemo(() => {
        const t: Record<StageKey, number> = {
            prepared: 0,
            initialReview: 0,
            managerReview: 0,
            sentForApproval: 0,
            approved: 0,
            filed: 0,
        };
        for (const r of filtered) {
            for (const s of STAGES) {
                t[s.key] += r[s.key];
            }
        }
        return t;
    }, [filtered]);

    // Latest month (for "this month" comparison on KPI cards).
    const latestMonth = filtered[filtered.length - 1];

    // ── Chart data ─────────────────────────────────────────
    const trendChartData = useMemo(
        () => ({
            labels: filtered.map((r) => r.month),
            datasets: STAGES.map((s) => ({
                label: s.short,
                data: filtered.map((r) => r[s.key]),
                borderColor: s.color,
                backgroundColor: s.color + "33",
                tension: 0.3,
                borderWidth: 2,
                pointRadius: 2,
                pointHoverRadius: 5,
            })),
        }),
        [filtered]
    );

    const stageTotalsChartData = useMemo(
        () => ({
            labels: STAGES.map((s) => s.short),
            datasets: [
                {
                    label: yearFilter === "all" ? "All years" : String(yearFilter),
                    data: STAGES.map((s) => totals[s.key]),
                    backgroundColor: STAGES.map((s) => s.color),
                    borderWidth: 0,
                },
            ],
        }),
        [totals, yearFilter]
    );

    const chartCommonOptions = useMemo(
        () => ({
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
                legend: { labels: { color: "rgba(226,232,240,0.85)", boxWidth: 12, font: { size: 11 } } },
                tooltip: { mode: "index" as const, intersect: false },
            },
            scales: {
                x: { ticks: { color: "rgba(226,232,240,0.6)", font: { size: 10 } }, grid: { color: "rgba(255,255,255,0.05)" } },
                y: { ticks: { color: "rgba(226,232,240,0.6)" }, grid: { color: "rgba(255,255,255,0.05)" }, beginAtZero: true },
            },
            interaction: { mode: "nearest" as const, axis: "x" as const, intersect: false },
        }),
        []
    );

    // ── Table columns ──────────────────────────────────────
    const columns: TableColumn<Row>[] = [
        {
            name: "Month",
            selector: (row) => row.monthDate.getTime(),
            sortable: true,
            cell: (row) => <span className="cell-primary">{row.month}</span>,
            width: "100px",
        },
        ...STAGES.map((s) => ({
            name: s.label,
            selector: (row: Row) => row[s.key],
            sortable: true,
            right: true,
            cell: (row: Row) => <span className="cell-value">{row[s.key]}</span>,
        })),
        {
            name: "Total",
            selector: (row) => row.total,
            sortable: true,
            right: true,
            cell: (row) => <span className="cell-value accent">{row.total}</span>,
        },
    ];

    // ── Exports ────────────────────────────────────────────
    const downloadCSV = () => {
        const headers = ["Month", ...STAGES.map((s) => s.label), "Total"];
        const rows = filtered.map((r) =>
            [r.month, ...STAGES.map((s) => r[s.key]), r.total].join(",")
        );
        const csv = [headers.join(","), ...rows].join("\n");
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = `corporation_tax_tracker_${yearFilter === "all" ? "all" : yearFilter}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const downloadXLSX = () => {
        const wb = XLSX.utils.book_new();
        const data = filtered.map((r) => {
            const out: Record<string, any> = { Month: r.month };
            for (const s of STAGES) out[s.label] = r[s.key];
            out["Total"] = r.total;
            return out;
        });
        XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(data), "Monthly Counts");

        const totalsSheet = STAGES.map((s) => ({ Stage: s.label, Count: totals[s.key] }));
        totalsSheet.push({
            Stage: "TOTAL",
            Count: Object.values(totals).reduce((a, b) => a + b, 0),
        });
        XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(totalsSheet), "Stage Totals");

        XLSX.writeFile(wb, `corporation_tax_tracker_${yearFilter === "all" ? "all" : yearFilter}.xlsx`);
    };

    // ── Render ─────────────────────────────────────────────
    return (
        <div className="page-container">
            <div className="page-header compact">
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <div className="page-title">
                        <Calculator size={20} />
                        <h1>Corporation Tax Tracker</h1>
                    </div>
                    <div style={{ display: "flex", gap: 8 }}>
                        <button
                            className="download-btn"
                            onClick={() => setShowHelp((v) => !v)}
                            title="Notes on Corporation Tax Tracker"
                        >
                            <Info size={16} /> {showHelp ? "Hide details" : "Notes on this Report"}
                        </button>
                        <button className="download-btn" onClick={downloadCSV} title="Export as CSV">
                            <Download size={16} /> CSV
                        </button>
                        <button className="download-btn" onClick={downloadXLSX} title="Export as XLSX">
                            <Download size={16} /> XLSX
                        </button>
                    </div>
                </div>
            </div>

            {showHelp && (
                <div
                    style={{
                        background: "var(--bg-secondary)",
                        border: "1px solid var(--border-color)",
                        borderLeft: "3px solid var(--accent-primary)",
                        borderRadius: 6,
                        padding: "14px 18px",
                        marginBottom: 20,
                        color: "var(--text-secondary)",
                        fontSize: 13,
                        lineHeight: 1.55,
                    }}
                >
                    <div
                        style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                            marginBottom: 8,
                            color: "var(--text-primary)",
                            fontWeight: 600,
                            fontSize: 14,
                        }}
                    >
                        <Info size={16} /> About this report
                    </div>
                    <p style={{ margin: "0 0 10px" }}>
                        This report replaces the manual monthly tracking spreadsheet used to monitor the
                        corporation tax workflow. Each row represents one calendar month; each column is
                        a count of <em>distinct companies</em> that reached a given stage that month.
                    </p>
                    <ul style={{ margin: "0 0 10px 20px", padding: 0 }}>
                        <li>
                            <strong style={{ color: "var(--text-primary)" }}>Accounts Prepared</strong> —
                            companies whose accounts were drafted/closed off during the month.
                        </li>
                        <li>
                            <strong style={{ color: "var(--text-primary)" }}>Initial Review Completed</strong> —
                            first reviewer signed off on the accounts that month.
                        </li>
                        <li>
                            <strong style={{ color: "var(--text-primary)" }}>Managers Review Completed</strong> —
                            manager signed off after the initial review.
                        </li>
                        <li>
                            <strong style={{ color: "var(--text-primary)" }}>Accounts Sent for Approval</strong> —
                            issued to the client for sign-off.
                        </li>
                        <li>
                            <strong style={{ color: "var(--text-primary)" }}>Accounts Approved</strong> —
                            client approval received.
                        </li>
                        <li>
                            <strong style={{ color: "var(--text-primary)" }}>Tax Return Filed</strong> —
                            CT1 filed with Revenue.
                        </li>
                    </ul>
                    <p style={{ margin: "0 0 0", color: "var(--text-muted)", fontSize: 12 }}>
                        Data source: <code>task_instances</code> in the Senta Explorer Postgres, filtered
                        to <code>status = 'completed'</code>. Each row counts <em>distinct companies</em>{" "}
                        per stage per month (based on <code>updated_at</code>'s month).
                    </p>
                </div>
            )}

            {/* Year filter */}
            <div className="table-controls" style={{ marginBottom: 20 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                    {(["all", ...years] as (number | "all")[]).map((y) => {
                        const active = yearFilter === y;
                        return (
                            <button
                                key={String(y)}
                                className={`filter-select${active ? " active" : ""}`}
                                style={{
                                    cursor: "pointer",
                                    padding: "6px 12px",
                                    border: active ? "1px solid var(--accent-primary)" : "1px solid var(--border-color)",
                                    borderRadius: 6,
                                    background: active ? "var(--accent-primary)" : "var(--bg-secondary)",
                                    color: active ? "#fff" : "var(--text-secondary)",
                                    fontWeight: active ? 600 : 400,
                                    fontSize: 13,
                                }}
                                onClick={() => setYearFilter(y)}
                            >
                                {y === "all" ? "All Years" : y}
                            </button>
                        );
                    })}
                </div>
                <div style={{ marginLeft: "auto", color: "var(--text-muted)", fontSize: 12 }}>
                    {filtered.length} month{filtered.length === 1 ? "" : "s"} shown
                </div>
            </div>

            {/* KPI strip — one card per stage, plus latest-month context */}
            <div
                style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                    gap: 12,
                    marginBottom: 20,
                }}
            >
                {STAGES.map((s) => (
                    <div
                        key={s.key}
                        style={{
                            background: "var(--bg-secondary)",
                            border: "1px solid var(--border-color)",
                            borderLeft: `3px solid ${s.color}`,
                            borderRadius: 8,
                            padding: 14,
                        }}
                    >
                        <div
                            style={{
                                color: "var(--text-secondary)",
                                fontSize: 12,
                                marginBottom: 4,
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                            }}
                            title={s.label}
                        >
                            {s.label}
                        </div>
                        <div style={{ fontSize: 24, fontWeight: 600, color: "var(--text-primary)" }}>
                            {totals[s.key].toLocaleString("en-IE")}
                        </div>
                        <div style={{ color: "var(--text-muted)", fontSize: 12, marginTop: 2 }}>
                            {latestMonth ? `${latestMonth.month}: ${latestMonth[s.key]}` : "—"}
                        </div>
                    </div>
                ))}
            </div>

            {/* Charts */}
            <div
                style={{
                    display: "grid",
                    gridTemplateColumns: "minmax(0, 2fr) minmax(0, 1fr)",
                    gap: 16,
                    marginBottom: 20,
                }}
            >
                <div
                    style={{
                        background: "var(--bg-secondary)",
                        border: "1px solid var(--border-color)",
                        borderRadius: 8,
                        padding: 14,
                    }}
                >
                    <div
                        style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                            marginBottom: 12,
                            color: "var(--text-primary)",
                            fontWeight: 600,
                            fontSize: 14,
                        }}
                    >
                        <TrendingUp size={16} /> Monthly trend by stage
                    </div>
                    <div style={{ height: 320 }}>
                        <Line data={trendChartData} options={chartCommonOptions} />
                    </div>
                </div>

                <div
                    style={{
                        background: "var(--bg-secondary)",
                        border: "1px solid var(--border-color)",
                        borderRadius: 8,
                        padding: 14,
                    }}
                >
                    <div
                        style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                            marginBottom: 12,
                            color: "var(--text-primary)",
                            fontWeight: 600,
                            fontSize: 14,
                        }}
                    >
                        <BarChart3 size={16} /> Stage totals
                    </div>
                    <div style={{ height: 320 }}>
                        <Bar
                            data={stageTotalsChartData}
                            options={{
                                ...chartCommonOptions,
                                plugins: { ...chartCommonOptions.plugins, legend: { display: false } },
                            }}
                        />
                    </div>
                </div>
            </div>

            {error && (
                <div
                    style={{
                        background: "var(--bg-secondary)",
                        border: "1px solid var(--status-error, #d9534f)",
                        borderRadius: 6,
                        padding: "10px 14px",
                        marginBottom: 12,
                        color: "var(--status-error, #d9534f)",
                    }}
                >
                    {error}
                </div>
            )}

            {/* Data table (exportable, sortable) */}
            <div
                style={{
                    background: "var(--bg-secondary)",
                    borderRadius: 8,
                    border: "1px solid var(--border-color)",
                }}
            >
                <DataTable
                    columns={columns}
                    data={filtered}
                    customStyles={customStyles}
                    pagination
                    paginationPerPage={25}
                    paginationRowsPerPageOptions={[10, 25, 50, 100]}
                    highlightOnHover
                    defaultSortFieldId={1}
                    defaultSortAsc={false}
                    noDataComponent={
                        <div style={{ padding: 40, textAlign: "center", color: "var(--text-muted)" }}>
                            No data for the selected year.
                        </div>
                    }
                />
            </div>
        </div>
    );
};

export default CorporationTaxTracker;
